import {BufferAttribute, BufferGeometry, LineBasicMaterial, LineSegments, Vector3} from 'three';
import { isDevMode } from '../../../electron-modules';
import { PointCloudMaterial } from '../rendering';
import {
  CloudNode,
  RawPointCloudRootNode,
  PointCloudManager
} from '.';
import { closestPowerOfTwo, mergeBoundingBoxes, randomHexColor } from '../utilities';

export class PointCloud {
  public uniqueClassifications: Set<any>;
  private readonly rgbArray = [];
  public maxColor = null;
  public intensityRange = null;
  private name = null;
  public identifier = null;
  private empty = false;
  public missing = false;
  protected enabled = true;
  public visible = true;
  public materialScene: PointCloudMaterial = null;
  public materialPicker: PointCloudMaterial = null;
  public materials: PointCloudMaterial[];
  public root = null;
  protected pointclouds: PointCloudManager;
  protected _tightBoundingBox = null;
  public debugBoxColor = randomHexColor();

  constructor(pointclouds) {
    this.pointclouds = pointclouds;
    this.uniqueClassifications = new Set();

    this.materialScene = new PointCloudMaterial(this.viewer,
      this.pointclouds.materialDefaults, false);

    this.materialPicker = new PointCloudMaterial(this.viewer,
      this.pointclouds.materialDefaults, true);

    this.materials = [
      this.materialScene,
      this.materialPicker
    ];
  }

  get shift() {
    return new Vector3();
  }

  get size() {
    return 0;
  }

  get viewer() {
    return this.pointclouds.viewer;
  }

  get scene() {
    return this.pointclouds.scene;
  }

  get picker() {
    return this.viewer.gpuPickers.default;
  }

  /** Get bounding box with global shift applied */
  get tightBoundingBox() {
    if (this._tightBoundingBox === null) {
      return this._tightBoundingBox;
    }

    let boundingBox = {
      min: new Vector3().add(this._tightBoundingBox.min).add(this.shift),
      max: new Vector3().add(this._tightBoundingBox.max).add(this.shift)
    };

    return boundingBox;
  }

  setName(name) {
    this.name = name;
  }

  setState(state) {
    this.enabled = state;
  }

  setVisibility(visible) {
    const changed = this.visible !== visible;
    this.visible = visible;
    if (changed) {
      this.pointclouds.updateMinimapExtent();
    }
  }

  setEmpty(state) {
    this.empty = state;
  }

  setIdentifier(identifier) {
    // console.log(`Added pointcloud [id = ${identifier}]`);
    identifier = identifier === undefined ? null : identifier;
    this.identifier = identifier;
  }

  /** @private */
  destroyWorker() {
    console.warn("Not implemented");
  }

  dispose() {
    let nodes = this.getVisibleNodes();
    this.setState(false);
    nodes.forEach(node => {
      node.visible = false;
      node.dispose();
    });

    this.destroyWorker();
  }

  getVisibleNodes(nodeCondition = null) : CloudNode[] {
    let visibleNodes = [];
    if (!this.root) {
      return visibleNodes;
    }

    let stack = [this.root];
    while (stack.length > 0) {
      let object = stack.shift();
      if (!object.visible) {
        continue;
      }

      if (nodeCondition) {
        if (nodeCondition(object) === false) {
          continue;
        }
      }

      if ((object instanceof RawPointCloudRootNode) === false) {
        visibleNodes.push(object);
      }

      object.children.forEach(child => {
        stack.push(child);
      });
    }

    return visibleNodes;
  }

  updateBoundingBox(boundingBox) {
    let combined = mergeBoundingBoxes(this._tightBoundingBox, boundingBox);
    this._tightBoundingBox = combined;
  }

  updateIntensityRange(intensityMin, intensityMax) {
    if (this.intensityRange === null) {
      this.intensityRange = {
        min: intensityMin,
        max: intensityMax
      };
    }

    this.intensityRange.min = Math.min(intensityMin, this.intensityRange.min);
    this.intensityRange.max = Math.max(intensityMax, this.intensityRange.max);
  }

  updateColorLimits(rgbMax) {
    let closestValue = closestPowerOfTwo(rgbMax);
    this.rgbArray.push(closestValue);
    this.rgbArray.sort();

    let arrMax = Math.max(...this.rgbArray);
    let arrMin = Math.min(...this.rgbArray);
    let diff = arrMax - arrMin;
    if (diff === 0) {
      this.maxColor = arrMax;
      return;
    }

    // Grab the middle value instead
    let midpoint = this.rgbArray.length / 2.0;
    midpoint = Math.ceil(midpoint);
    this.maxColor = this.rgbArray[midpoint];
  }

  updateClassifications(classification) {
    classification = new Set(classification);
    classification.forEach(item => {
      this.uniqueClassifications.add(item);
    });
  }

  generateEdgeMesh = (sizeX, sizeY, sizeZ) => {
    if (!isDevMode) {
      // Only generated in developer mode
      return null;
    }

    let vertexArray = [
      -0.5, 0.5, -0.5,
      -0.5, 0.5, 0.5,
      0.5, 0.5, 0.5,
      0.5, 0.5, -0.5,
      -0.5, -0.5, 0.5,
      -0.5, -0.5, -0.5,
      0.5, -0.5, -0.5,
      0.5, -0.5, 0.5,
      -0.5, 0.5, 0.5,
      -0.5, -0.5, 0.5,
      0.5, 0.5, 0.5,
      -0.5, 0.5, 0.5,
      -0.5, -0.5, 0.5,
      0.5, -0.5, 0.5,
      0.5, -0.5, 0.5,
      0.5, 0.5, 0.5,
      0.5, 0.5, -0.5,
      0.5, -0.5, -0.5,
      -0.5, 0.5, -0.5,
      0.5, 0.5, -0.5,
      0.5, -0.5, -0.5,
      -0.5, -0.5, -0.5,
      -0.5, -0.5, -0.5,
      -0.5, 0.5, -0.5
    ];

    const edgeVertices = new Float32Array(vertexArray.length);
    vertexArray.forEach((v,index) => {
      let scale;
      switch(index % 3) {
      case 0:
        scale = sizeX;
        break;
      case 1:
        scale = sizeY;
        break;
      case 2:
        scale = sizeZ;
        break;
      default:
        scale = 1.0;
      }

      edgeVertices[index] = v * scale;
    });

    const boxGeometry = new BufferGeometry();
    boxGeometry.setAttribute('position',
      new BufferAttribute(edgeVertices, 3));
    boxGeometry.center();

    let boxMaterial = new LineBasicMaterial({color: this.debugBoxColor});
    let boundingBox = new LineSegments(boxGeometry, boxMaterial);

    boundingBox.visible = false;

    return boundingBox;
  }
}
